import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    data: null,
    isLoading: false,
    error: null,
    page: 1,
    dispatchingWave: null,
    dispatchError: null,
    lastDispatch: null,
};

const commandCentreSlice = createSlice({
    name: 'commandCentre',
    initialState,
    reducers: {
        fetchCommandCentre: state => {
            state.isLoading = true;
            state.error = null;
        },
        commandCentreFetched: (state, { payload }) => {
            state.data = payload;
            state.isLoading = false;
            state.error = null;
        },
        commandCentreFetchFailed: (state, { payload }) => {
            state.isLoading = false;
            state.error = payload;
        },
        setPage: (state, { payload }) => {
            state.page = payload;
        },
        /**
         * Local-only mutation after a successful hire. Moves the contractor
         * from the recommended list into the hired bucket and bumps filled
         * count, so the page reflects the new state without paying the cost
         * of a full eligibility recompute.
         */
        applicantHired: (state, { payload }) => {
            if (!state.data) return;
            const { contractorId, firstName, lastName, distanceKm } = payload;
            const elig = state.data.eligibility;
            if (elig && Array.isArray(elig.items)) {
                const beforeLen = elig.items.length;
                elig.items = elig.items.filter(
                    i => i.contractorId !== contractorId,
                );
                if (elig.items.length < beforeLen) {
                    elig.eligibleCount = Math.max(
                        0,
                        (elig.eligibleCount || 0) - 1,
                    );
                }
            }
            if (state.data.applicants) {
                const hired = state.data.applicants.hired || [];
                if (!hired.some(h => h.contractorId === contractorId)) {
                    state.data.applicants.hired = [
                        ...hired,
                        {
                            contractorId,
                            firstName,
                            lastName,
                            state: 'accepted',
                            distanceKm,
                        },
                    ];
                }
            }
            if (state.data.job) {
                state.data.job.filled = (state.data.job.filled || 0) + 1;
            }
        },
        /**
         * Local-only mutation after a successful reject. Moves the contractor
         * from interested → rejected and from eligibility.items (so the recs
         * list stops surfacing them) without paying the cost of a recompute.
         */
        applicantRejected: (state, { payload }) => {
            if (!state.data) return;
            const { contractorId, firstName, lastName, reason } = payload;
            const apps = state.data.applicants;
            if (apps) {
                if (Array.isArray(apps.interested)) {
                    apps.interested = apps.interested.filter(
                        i => i.contractorId !== contractorId,
                    );
                }
                const rejected = apps.rejected || [];
                if (!rejected.some(r => r.contractorId === contractorId)) {
                    apps.rejected = [
                        ...rejected,
                        { contractorId, firstName, lastName, reason },
                    ];
                }
            }
            const elig = state.data.eligibility;
            if (elig && Array.isArray(elig.items)) {
                const beforeLen = elig.items.length;
                elig.items = elig.items.filter(
                    i => i.contractorId !== contractorId,
                );
                if (elig.items.length < beforeLen) {
                    elig.eligibleCount = Math.max(
                        0,
                        (elig.eligibleCount || 0) - 1,
                    );
                }
            }
        },
        /**
         * Ops clicked "Approve & Send Wave N" and confirmed. Saga will POST
         * to /dispatch-cohort. Track which wave is in flight so the panel can
         * disable the button + show a spinner.
         */
        dispatchCohortRequested: (state, { payload }) => {
            state.dispatchingWave = payload && payload.wave;
            state.dispatchError = null;
        },
        dispatchCohortSucceeded: (state, { payload }) => {
            state.dispatchingWave = null;
            state.dispatchError = null;
            state.lastDispatch = {
                wave: payload && payload.wave,
                dispatched: payload && payload.dispatched,
                windowExpiresAt: payload && payload.windowExpiresAt,
                at: new Date().toISOString(),
            };
        },
        dispatchCohortFailed: (state, { payload }) => {
            state.dispatchingWave = null;
            state.dispatchError = payload && payload.error;
        },
        /**
         * Local-only mutation right after a successful dispatch — flips the
         * wave card to "sent" before the BFF refetch lands. Subsequent waves'
         * candidate lists are refreshed only after the refetch completes.
         */
        optimisticWaveSent: (state, { payload }) => {
            if (!state.data || !state.data.cohorts) return;
            const wave = payload && payload.wave;
            const key = `wave${wave}`;
            const w = state.data.cohorts.waves && state.data.cohorts.waves[key];
            if (!w) return;
            const totalCandidates = w.candidates ? w.candidates.length : 0;
            const excludedCount =
                payload && Array.isArray(payload.excludeIds)
                    ? payload.excludeIds.length
                    : 0;
            const sentCount = Math.max(0, totalCandidates - excludedCount);
            w.state = 'sent';
            w.dispatched = {
                dispatchedAt:
                    (payload && payload.dispatchedAt) ||
                    new Date().toISOString(),
                sentCount,
                statusBreakdown: { queued: sentCount },
            };
        },
        clearCommandCentre: () => initialState,
    },
});

export const {
    fetchCommandCentre,
    commandCentreFetched,
    commandCentreFetchFailed,
    setPage,
    applicantHired,
    applicantRejected,
    dispatchCohortRequested,
    dispatchCohortSucceeded,
    dispatchCohortFailed,
    optimisticWaveSent,
    clearCommandCentre,
} = commandCentreSlice.actions;

export default commandCentreSlice.reducer;
